import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router";

import type {
  AccountOrder,
  AccountSort,
} from "@/src/pages/Accounts/accounts.type";
import {
  isAccountOrder,
  isAccountSort,
} from "@/src/pages/Accounts/accounts.type";

export type AccountFilters = {
  q: string;
  page: number;
  sort: AccountSort;
  order: AccountOrder;
  /** Centre de recherche — les trois vont ensemble ou pas du tout. */
  lat: number | null;
  lng: number | null;
  radius: number;
  /**
   * Le lieu tel qu'il a été choisi (« Lyon, France »).
   *
   * ⚠️ **Il n'est pas envoyé au serveur**, qui ne connaît que des coordonnées.
   * Il vit dans l'URL pour deux raisons : réafficher le champ après un retour
   * arrière, et dire *où* l'on cherche — `45.76 / 4.83` ne se relit pas.
   */
  near: string;
};

/** Rayon par défaut, appliqué **seulement** quand un centre est donné. */
export const DEFAULT_RADIUS = 50;

function readNumber(value: string | null, min: number, max: number) {
  if (value === null) return null;

  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < min || parsed > max) return null;

  return parsed;
}

/**
 * Les filtres de la liste, portés par **l'URL**.
 *
 * ⚠️ **Et non par un `useState`, contrairement au filtre des signalements.**
 * La différence n'est pas cosmétique : d'ici on ouvre une fiche, et le retour
 * arrière du navigateur démonte l'écran. Avec un état local, l'administrateur
 * retrouverait la première page d'une recherche vide après chaque consultation
 * — c'est-à-dire tout perdre à chaque compte examiné.
 *
 * Le prix à payer, c'est de ne rien croire de ce qui vient de la barre
 * d'adresse : chaque valeur est relue et bornée ici, jamais passée telle quelle
 * à la requête.
 */
export function useAccountFilters() {
  const [params, setParams] = useSearchParams();

  const filters = useMemo<AccountFilters>(() => {
    const lat = readNumber(params.get("lat"), -90, 90);
    const lng = readNumber(params.get("lng"), -180, 180);

    // Une latitude sans longitude est refusée par le serveur (400), et à juste
    // titre : une requête à moitié géographique rendrait une liste plus large
    // que demandé sans le dire. On la traite ici comme une absence de centre.
    const hasCenter = lat !== null && lng !== null;

    // La garde rétrécit la valeur lue, elle ne fait pas que la tester : c'est
    // ce qui permet de passer `sort` à la requête sans le recaster.
    const sort = params.get("sort");
    const order = params.get("order");

    return {
      q: params.get("q") ?? "",
      page: readNumber(params.get("page"), 1, 10_000) ?? 1,
      sort: isAccountSort(sort) ? sort : "createdAt",
      order: isAccountOrder(order) ? order : "desc",
      lat: hasCenter ? lat : null,
      lng: hasCenter ? lng : null,
      radius: readNumber(params.get("radius"), 1, 500) ?? DEFAULT_RADIUS,
      near: hasCenter ? (params.get("near") ?? "") : "",
    };
  }, [params]);

  /**
   * Change un ou plusieurs filtres, et **revient page 1** — sauf quand c'est la
   * page elle-même qu'on change. Sans cette remise à zéro, filtrer depuis la
   * page 4 afficherait la page 4 d'un résultat qui n'en compte qu'une, donc du
   * vide qui ressemble à « aucun compte ».
   */
  const update = useCallback(
    (patch: Partial<AccountFilters>) => {
      setParams(
        (previous) => {
          const next = new URLSearchParams(previous);

          for (const [key, value] of Object.entries(patch)) {
            if (value === null || value === "") next.delete(key);
            else next.set(key, String(value));
          }

          if (!("page" in patch)) next.delete("page");

          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  return { filters, update };
}
